/**
 * Start Cycle Count Drawer
 * 
 * Drawer for initiating a new cycle count on a warehouse or vehicle.
 * Handles cycle count type selection (Full/Category) and category selection.
 * 
 * Note: Backend uses /api/audits routes (alias approach - DB tables remain unchanged)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Loader2, ClipboardCheck, Package, FolderOpen } from 'lucide-react';

export function StartCycleCountModal({ 
  open, 
  onOpenChange, 
  targetType,
  targetId,
  targetName,
  returnTo
}) {
  const navigate = useNavigate();
  const [cycleCountType, setCycleCountType] = useState('full');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (open && cycleCountType === 'category') loadCategories();
  }, [open, cycleCountType]);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleStartCycleCount = async () => {
    if (cycleCountType === 'category' && selectedCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    setStarting(true);
    try {
      const response = await api.post('/audits', {
        audit_type: cycleCountType,
        target_type: targetType,
        target_id: targetId,
        category_ids: cycleCountType === 'category' ? selectedCategories : null
      });
      toast.success(`Cycle count started with ${response.data.total_items} items`);
      onOpenChange(false);
      const returnParam = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
      navigate(`/cycle-count/${response.data.id}${returnParam}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.code === 'PLAN_LIMIT_EXCEEDED') {
        toast.error('Plan limit exceeded');
      } else {
        toast.error(detail || 'Failed to start cycle count');
      }
    } finally {
      setStarting(false);
    }
  };

  const handleClose = () => {
    setCycleCountType('full');
    setSelectedCategories([]);
    onOpenChange(false);
  };

  const isDirty = selectedCategories.length > 0;

  return (
    <AppDrawer
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); else onOpenChange(nextOpen); }}
      title={
        <span className="flex items-center gap-2">
          <ClipboardCheck className="w-5 h-5 text-primary" />
          Start Cycle Count
        </span>
      }
      description={`${targetType === 'warehouse' ? 'Warehouse' : 'Vehicle'}: ${targetName || ''}`}
      size="md"
      isDirty={isDirty}
      data-testid="start-cycle-count-drawer"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} data-testid="cycle-count-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleStartCycleCount} disabled={starting} data-testid="start-cycle-count-btn">
            {starting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ClipboardCheck className="w-4 h-4 mr-2" />
            )}
            Start Cycle Count
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Cycle Count Type Selection */}
        <div className="space-y-3">
          <Label className="text-sm font-bold uppercase tracking-wide">Cycle Count Type</Label>
          <RadioGroup value={cycleCountType} onValueChange={setCycleCountType}>
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="full" id="full" className="mt-0.5" />
              <div className="flex-1">
                <label htmlFor="full" className="flex items-center gap-2 cursor-pointer">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Full Cycle Count</span>
                </label>
                <p className="text-sm text-muted-foreground mt-1">Count all items in inventory</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="category" id="category" className="mt-0.5" />
              <div className="flex-1">
                <label htmlFor="category" className="flex items-center gap-2 cursor-pointer">
                  <FolderOpen className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">Category Cycle Count</span>
                </label>
                <p className="text-sm text-muted-foreground mt-1">Count items in selected categories only</p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Category Selection */}
        {cycleCountType === 'category' && (
          <div className="space-y-3">
            <Label className="text-sm font-bold uppercase tracking-wide">
              Select Categories <span className="text-destructive">*</span>
            </Label>
            {loadingCategories ? (
              <div className="flex items-center justify-center p-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : categories.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center border rounded-lg">No categories found</p>
            ) : (
              <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                {categories.map(category => (
                  <div key={category.id}
                    className="flex items-center space-x-3 p-3 hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleCategoryToggle(category.id)}>
                    <Checkbox id={category.id} checked={selectedCategories.includes(category.id)}
                      onCheckedChange={() => handleCategoryToggle(category.id)} />
                    <label htmlFor={category.id} className="flex-1 cursor-pointer text-sm font-medium">
                      {category.name}
                    </label>
                  </div>
                ))}
              </div>
            )}
            {selectedCategories.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedCategories.length} categor{selectedCategories.length === 1 ? 'y' : 'ies'} selected
              </p>
            )}
          </div>
        )}
      </div>
    </AppDrawer>
  );
}

export default StartCycleCountModal;
